import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  SpinnerGap, Plus, Trash, ShieldCheck, Clock,
  Users, CurrencyDollar, Tag, Warning,
  ToggleLeft, ToggleRight, ChartBar, ListBullets,
  TrendUp, Percent, CalendarBlank, ArrowsClockwise
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const DURATION_LABELS = { once: "Once", repeating: "Multiple months", forever: "Forever" };
const STATUS_COLORS = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  disabled: "bg-[var(--bg-raised)] text-[var(--text-muted)] border-[var(--border)]",
  expired: "bg-red-500/10 text-red-400 border-red-500/20",
  maxed: "bg-amber-500/10 text-amber-400 border-amber-500/20",
};

export function AdminCouponsSection() {
  const { api } = useAuth();
  const [view, setView] = useState("analytics");
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [form, setForm] = useState({
    name: "", code: "", discount_type: "percent", discount_value: "",
    duration: "once", duration_in_months: "", max_redemptions: "",
    expires_at: "", first_time_only: false, minimum_amount: "",
  });

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get("/admin/coupons");
      setCoupons(resp.data.coupons || []);
    } catch (e) {
      toast.error("Failed to load coupons");
    }
    setLoading(false);
  }, [api]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const resp = await api.get("/admin/coupons/analytics");
      setAnalytics(resp.data);
    } catch (e) {
      toast.error("Failed to load coupon analytics");
    }
    setAnalyticsLoading(false);
  }, [api]);

  useEffect(() => { loadCoupons(); loadAnalytics(); }, [loadCoupons, loadAnalytics]);

  const resetForm = () => {
    setForm({ name: "", code: "", discount_type: "percent", discount_value: "", duration: "once", duration_in_months: "", max_redemptions: "", expires_at: "", first_time_only: false, minimum_amount: "" });
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.code.trim() || !form.discount_value) {
      toast.error("Name, code, and discount value are required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: form.name.trim(), code: form.code.trim(),
        discount_type: form.discount_type, discount_value: parseFloat(form.discount_value),
        duration: form.duration, first_time_only: form.first_time_only,
      };
      if (form.duration === "repeating" && form.duration_in_months) payload.duration_in_months = parseInt(form.duration_in_months);
      if (form.max_redemptions) payload.max_redemptions = parseInt(form.max_redemptions);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.minimum_amount) payload.minimum_amount = parseFloat(form.minimum_amount);
      const resp = await api.post("/admin/coupons", payload);
      toast.success(`Promo code "${resp.data.code}" created (${resp.data.discount} off)`);
      resetForm();
      loadCoupons();
      loadAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create coupon");
    }
    setCreating(false);
  };

  const handleToggle = async (promoId, currentActive) => {
    setToggling(promoId);
    try {
      await api.patch(`/admin/coupons/${promoId}`, { active: !currentActive });
      toast.success(currentActive ? "Promo code deactivated" : "Promo code reactivated");
      loadCoupons();
      loadAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to toggle coupon");
    }
    setToggling(null);
  };

  const handleDelete = async (couponId, code) => {
    if (!window.confirm(`Permanently delete coupon "${code}"? This cannot be undone.`)) return;
    setDeleting(couponId);
    try {
      await api.delete(`/admin/coupons/${couponId}`);
      toast.success("Coupon deleted");
      loadCoupons();
      loadAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete coupon");
    }
    setDeleting(null);
  };

  const formatDiscount = (c) => {
    if (c.percent_off) return `${c.percent_off}% off`;
    if (c.amount_off) return `$${(c.amount_off / 100).toFixed(0)} off`;
    return "—";
  };

  const formatDate = (ts) => {
    if (!ts) return null;
    return new Date(ts * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const activeCoupons = coupons.filter(c => c.active);
  const inactiveCoupons = coupons.filter(c => !c.active);

  return (
    <div className="space-y-4" data-testid="admin-coupons-section">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {[
            { id: "analytics", label: "Analytics", icon: ChartBar },
            { id: "manage", label: "Manage", icon: ListBullets },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setView(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                view === id
                  ? "bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/30"
                  : "text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-transparent"
              }`}
              data-testid={`coupon-view-${id}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {view === "manage" && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[#FF4400] text-white hover:bg-[#FF6633] transition-colors"
            data-testid="create-coupon-btn"
          >
            <Plus size={14} weight="bold" /> New Promo Code
          </button>
        )}
      </div>

      {/* ═══ ANALYTICS VIEW ═══ */}
      {view === "analytics" && (
        <AnalyticsView
          analytics={analytics}
          loading={analyticsLoading}
          onRefresh={loadAnalytics}
          formatDate={formatDate}
        />
      )}

      {/* ═══ MANAGE VIEW ═══ */}
      {view === "manage" && (
        <ManageView
          coupons={coupons}
          activeCoupons={activeCoupons}
          inactiveCoupons={inactiveCoupons}
          loading={loading}
          showForm={showForm}
          form={form}
          setForm={setForm}
          creating={creating}
          toggling={toggling}
          deleting={deleting}
          onCreate={handleCreate}
          onResetForm={resetForm}
          onToggle={handleToggle}
          onDelete={handleDelete}
          formatDiscount={formatDiscount}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  ANALYTICS VIEW                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

function AnalyticsView({ analytics, loading, onRefresh, formatDate }) {
  if (loading) {
    return <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>;
  }
  if (!analytics) {
    return <p className="text-sm text-[var(--text-muted)] text-center py-8">Unable to load analytics.</p>;
  }

  const s = analytics.summary;
  const perf = analytics.performance || [];
  const expiring = analytics.expiring_soon || [];
  const maxRedeemed = perf.length > 0 ? Math.max(...perf.map(p => p.times_redeemed), 1) : 1;

  return (
    <div className="space-y-4" data-testid="coupon-analytics">
      {/* Refresh */}
      <div className="flex justify-end">
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] transition-colors border border-[var(--border)]"
          data-testid="coupon-analytics-refresh"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Active Codes", value: s.active_codes, sub: `${s.total_codes} total`, icon: Tag, color: "text-emerald-400" },
          { label: "Total Redemptions", value: s.total_redemptions, sub: "all time", icon: Users, color: "text-[#FF4400]" },
          { label: "Avg Redemption Rate", value: `${s.avg_redemption_rate}%`, sub: "codes with limits", icon: Percent, color: s.avg_redemption_rate > 50 ? "text-emerald-400" : s.avg_redemption_rate > 20 ? "text-amber-400" : "text-[var(--text-muted)]" },
          { label: "Fraud Protected", value: `${s.fraud_protection_pct}%`, sub: `${s.fraud_protected}/${s.total_codes} codes`, icon: ShieldCheck, color: s.fraud_protection_pct >= 80 ? "text-emerald-400" : s.fraud_protection_pct >= 50 ? "text-amber-400" : "text-red-400" },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4" data-testid={`coupon-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className="text-[var(--text-muted)]" />
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium">{label}</span>
            </div>
            <span className={`text-2xl font-['JetBrains_Mono'] font-bold ${color}`}>{value}</span>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4" data-testid="coupon-status-breakdown">
          <h4 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Code Status</h4>
          <div className="space-y-2.5">
            {[
              { label: "Active", count: s.active_codes, color: "bg-emerald-500" },
              { label: "Disabled", count: s.inactive_codes, color: "bg-[var(--text-muted)]" },
              { label: "Expired", count: s.expired_codes, color: "bg-red-500" },
              { label: "Maxed Out", count: s.maxed_codes, color: "bg-amber-500" },
            ].map(({ label, count, color }) => {
              const pct = s.total_codes > 0 ? (count / s.total_codes) * 100 : 0;
              return (
                <div key={label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[var(--text-secondary)]">{label}</span>
                    <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-primary)]">{count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[var(--bg-base)] overflow-hidden">
                    <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expiring Soon Alert */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4" data-testid="coupon-expiring-soon">
          <div className="flex items-center gap-2 mb-3">
            <CalendarBlank size={14} className="text-amber-400" />
            <h4 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Expiring Within 7 Days</h4>
          </div>
          {expiring.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-4 text-center">No codes expiring soon</p>
          ) : (
            <div className="space-y-2">
              {expiring.map((e) => (
                <div key={e.code} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                  <div>
                    <span className="font-['JetBrains_Mono'] text-xs font-bold text-amber-400">{e.code}</span>
                    {e.name && <p className="text-[10px] text-[var(--text-muted)]">{e.name}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-amber-400 font-medium">{formatDate(e.expires_at)}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">{e.times_redeemed} used</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Performance Table */}
      {perf.length > 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4" data-testid="coupon-performance-table">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Performance by Code</h4>
            <span className="text-[10px] text-[var(--text-muted)]">Ranked by redemptions</span>
          </div>
          <div className="space-y-2">
            {perf.map((p, idx) => (
              <div
                key={p.code}
                className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors"
                data-testid={`coupon-perf-${p.code}`}
              >
                {/* Rank */}
                <span className={`font-['JetBrains_Mono'] text-xs font-bold w-6 text-center shrink-0 ${
                  idx === 0 ? "text-[#FF4400]" : idx < 3 ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"
                }`}>
                  #{idx + 1}
                </span>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-['JetBrains_Mono'] text-xs font-bold text-[var(--text-primary)]">{p.code}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{p.discount}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${STATUS_COLORS[p.status]}`}>
                      {p.status}
                    </span>
                    {p.first_time_only && (
                      <span className="flex items-center gap-0.5 text-[9px] text-emerald-400">
                        <ShieldCheck size={9} /> 1st-time
                      </span>
                    )}
                  </div>
                  {/* Redemption bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-card)] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#FF4400] transition-all duration-700"
                        style={{ width: `${(p.times_redeemed / maxRedeemed) * 100}%` }}
                      />
                    </div>
                    <span className="font-['JetBrains_Mono'] text-[10px] text-[var(--text-secondary)] shrink-0 w-16 text-right">
                      {p.times_redeemed}{p.max_redemptions ? `/${p.max_redemptions}` : ""} used
                    </span>
                  </div>
                </div>

                {/* Rate */}
                {p.redemption_rate !== null && (
                  <div className="text-right shrink-0">
                    <span className={`font-['JetBrains_Mono'] text-sm font-bold ${
                      p.redemption_rate >= 75 ? "text-emerald-400" : p.redemption_rate >= 40 ? "text-amber-400" : "text-[var(--text-muted)]"
                    }`}>
                      {p.redemption_rate}%
                    </span>
                    <p className="text-[9px] text-[var(--text-muted)]">used</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty analytics state */}
      {perf.length === 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 text-center" data-testid="coupon-analytics-empty">
          <ChartBar size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-muted)]">No promo codes to analyze yet. Create some in the Manage tab.</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  MANAGE VIEW                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function ManageView({
  coupons, activeCoupons, inactiveCoupons, loading,
  showForm, form, setForm, creating, toggling, deleting,
  onCreate, onResetForm, onToggle, onDelete, formatDiscount, formatDate,
}) {
  return (
    <div className="space-y-4">
      {/* Fraud Protection Summary */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-emerald-400" />
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Fraud Protection</span>
        </div>
        <p className="text-xs text-[var(--text-muted)] leading-relaxed">
          All promo codes support: max redemption limits, expiration dates, first-time customer restrictions, and minimum purchase thresholds. Stripe enforces these server-side.
        </p>
      </div>

      {/* Create Form */}
      {showForm && <CreateCouponForm form={form} setForm={setForm} creating={creating} onCreate={onCreate} onCancel={onResetForm} />}

      {/* Loading */}
      {loading && coupons.length === 0 && (
        <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>
      )}

      {/* Empty State */}
      {!loading && coupons.length === 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 text-center" data-testid="coupons-empty">
          <Tag size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-muted)]">No promo codes yet. Create your first one above.</p>
        </div>
      )}

      {/* Active Coupons */}
      {activeCoupons.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold px-1">Active ({activeCoupons.length})</h4>
          {activeCoupons.map((c) => (
            <CouponCard key={c.promo_id} coupon={c} formatDiscount={formatDiscount} formatDate={formatDate} onToggle={onToggle} onDelete={onDelete} toggling={toggling} deleting={deleting} />
          ))}
        </div>
      )}

      {/* Inactive Coupons */}
      {inactiveCoupons.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold px-1">Inactive ({inactiveCoupons.length})</h4>
          {inactiveCoupons.map((c) => (
            <CouponCard key={c.promo_id} coupon={c} formatDiscount={formatDiscount} formatDate={formatDate} onToggle={onToggle} onDelete={onDelete} toggling={toggling} deleting={deleting} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  CREATE FORM                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function CreateCouponForm({ form, setForm, creating, onCreate, onCancel }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[#FF4400]/30 rounded-xl p-5 space-y-4" data-testid="coupon-create-form">
      <h4 className="text-sm font-semibold text-[var(--text-primary)]">Create Promo Code</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Display Name</label>
          <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Spring Launch 20% Off" className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-name-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Promo Code</label>
          <input type="text" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. SPRING20" className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400] font-['JetBrains_Mono'] uppercase" data-testid="coupon-code-input" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Discount Type</label>
          <select value={form.discount_type} onChange={(e) => setForm(f => ({ ...f, discount_type: e.target.value }))} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-discount-type">
            <option value="percent">Percentage</option>
            <option value="amount">Fixed Amount (USD)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">{form.discount_type === "percent" ? "Percent Off" : "Amount Off ($)"}</label>
          <input type="number" min="0" max={form.discount_type === "percent" ? 100 : undefined} step="any" value={form.discount_value} onChange={(e) => setForm(f => ({ ...f, discount_value: e.target.value }))} placeholder={form.discount_type === "percent" ? "e.g. 20" : "e.g. 50"} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-discount-value" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Duration</label>
          <select value={form.duration} onChange={(e) => setForm(f => ({ ...f, duration: e.target.value }))} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-duration">
            <option value="once">Once (first invoice only)</option>
            <option value="repeating">Repeating (multiple months)</option>
            <option value="forever">Forever</option>
          </select>
        </div>
      </div>
      {form.duration === "repeating" && (
        <div className="max-w-xs">
          <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Number of Months</label>
          <input type="number" min="1" max="36" value={form.duration_in_months} onChange={(e) => setForm(f => ({ ...f, duration_in_months: e.target.value }))} placeholder="e.g. 3" className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-duration-months" />
        </div>
      )}
      <div className="border-t border-[var(--border)] pt-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={14} className="text-amber-400" />
          <span className="text-xs font-semibold text-[var(--text-secondary)]">Fraud Protection</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Max Redemptions</label>
            <input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm(f => ({ ...f, max_redemptions: e.target.value }))} placeholder="Unlimited if empty" className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-max-redemptions" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Expiration Date</label>
            <input type="date" value={form.expires_at} onChange={(e) => setForm(f => ({ ...f, expires_at: e.target.value }))} min={new Date().toISOString().split("T")[0]} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-expires-at" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium block mb-1">Min Purchase ($)</label>
            <input type="number" min="0" step="any" value={form.minimum_amount} onChange={(e) => setForm(f => ({ ...f, minimum_amount: e.target.value }))} placeholder="No minimum if empty" className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="coupon-min-amount" />
          </div>
          <div className="flex items-center gap-3 py-2">
            <button type="button" onClick={() => setForm(f => ({ ...f, first_time_only: !f.first_time_only }))} className={`relative w-10 h-5 rounded-full transition-colors ${form.first_time_only ? "bg-emerald-500" : "bg-[var(--bg-raised)] border border-[var(--border)]"}`} data-testid="coupon-first-time-toggle">
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.first_time_only ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <div>
              <span className="text-xs font-medium text-[var(--text-secondary)]">First-time customers only</span>
              <p className="text-[10px] text-[var(--text-muted)]">Only users who have never purchased before</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={onCreate} disabled={creating} className="bg-[#FF4400] hover:bg-[#E63E00] text-white text-xs gap-1.5" data-testid="coupon-submit-btn">
          {creating ? <><SpinnerGap size={14} className="animate-spin" /> Creating...</> : <><Tag size={14} /> Create Promo Code</>}
        </Button>
        <Button variant="outline" onClick={onCancel} className="text-xs" data-testid="coupon-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  COUPON CARD                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function CouponCard({ coupon: c, formatDiscount, formatDate, onToggle, onDelete, toggling, deleting }) {
  const isExpired = c.expires_at && c.expires_at * 1000 < Date.now();
  const isMaxed = c.max_redemptions && c.times_redeemed >= c.max_redemptions;

  return (
    <div
      className={`bg-[var(--bg-card)] border rounded-lg p-4 transition-all ${
        c.active && !isExpired && !isMaxed ? "border-[var(--border)]" : "border-[var(--border)] opacity-60"
      }`}
      data-testid={`coupon-row-${c.promo_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="font-['JetBrains_Mono'] text-sm font-bold text-[#FF4400]">{c.code}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--bg-raised)] text-[var(--text-secondary)] border border-[var(--border)]">{formatDiscount(c)}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border)]">
              {DURATION_LABELS[c.duration] || c.duration}{c.duration === "repeating" && c.duration_in_months ? ` (${c.duration_in_months}mo)` : ""}
            </span>
            {c.active && !isExpired && !isMaxed && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Active</span>}
            {isExpired && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20">Expired</span>}
            {isMaxed && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">Max Reached</span>}
            {!c.active && !isExpired && !isMaxed && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--bg-raised)] text-[var(--text-muted)] border border-[var(--border)]">Disabled</span>}
          </div>
          {c.name && <p className="text-xs text-[var(--text-secondary)] mb-2">{c.name}</p>}
          <div className="flex items-center gap-3 flex-wrap text-[10px] text-[var(--text-muted)]">
            {c.max_redemptions ? (
              <span className="flex items-center gap-1" data-testid={`coupon-uses-${c.promo_id}`}><Users size={11} /> {c.times_redeemed}/{c.max_redemptions} used</span>
            ) : (
              <span className="flex items-center gap-1"><Users size={11} /> {c.times_redeemed} used (unlimited)</span>
            )}
            {c.expires_at && <span className="flex items-center gap-1"><Clock size={11} /> Expires {formatDate(c.expires_at)}</span>}
            {c.first_time_only && <span className="flex items-center gap-1 text-emerald-400"><ShieldCheck size={11} /> First-time only</span>}
            {c.minimum_amount > 0 && <span className="flex items-center gap-1"><CurrencyDollar size={11} /> Min ${(c.minimum_amount / 100).toFixed(0)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => onToggle(c.promo_id, c.active)} disabled={toggling === c.promo_id} className={`p-1.5 rounded-md transition-colors ${c.active ? "text-emerald-400 hover:bg-emerald-500/10" : "text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"}`} title={c.active ? "Deactivate" : "Reactivate"} data-testid={`coupon-toggle-${c.promo_id}`}>
            {toggling === c.promo_id ? <SpinnerGap size={16} className="animate-spin" /> : c.active ? <ToggleRight size={16} weight="fill" /> : <ToggleLeft size={16} />}
          </button>
          <button onClick={() => onDelete(c.coupon_id, c.code)} disabled={deleting === c.coupon_id} className="p-1.5 rounded-md text-red-400 hover:bg-red-400/10 transition-colors" title="Delete permanently" data-testid={`coupon-delete-${c.promo_id}`}>
            {deleting === c.coupon_id ? <SpinnerGap size={16} className="animate-spin" /> : <Trash size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
